/**
 * SubHeaderSection — dispatcher for the per-entity-type sub-header content
 * rendered inside the RecordHeader.
 *
 * Per-entity rendering lives on the entity type's registry entry under
 * `components.subHeader`. This component is a registry consult; adding a
 * new entity-type-specific sub-header is a one-line edit to
 * `entityTypeRegistry.ts`. Returns null for any entity type without a
 * registered sub-header — that's the default (no sub-header is shown).
 *
 * Special case: `ahoy_file` is not registered in ENTITY_TYPE_REGISTRY
 * (it falls back to FALLBACK_CONFIG which has no subHeader), but files do
 * show the access_summary privacy badge via AccessBadgeSubHeader.
 */

import type { ReactElement } from 'react';

import type { CrmObject } from '@/types/crm';

import { getEntityTypeConfig } from '../../record/registry/entityTypeRegistry';
import { AccessBadgeSubHeader } from './access-badge-sub-header';

interface SubHeaderSectionProps {
  object: CrmObject;
}

export function SubHeaderSection({
  object,
}: SubHeaderSectionProps): ReactElement | null {
  const SubHeader = getEntityTypeConfig(object.entity_type_name).components
    ?.subHeader;
  if (SubHeader) {
    return <SubHeader object={object} />;
  }
  // ahoy_file is not registered in ENTITY_TYPE_REGISTRY but does show
  // the access_summary privacy badge when present.
  if (object.entity_type_name === 'ahoy_file') {
    return <AccessBadgeSubHeader object={object} />;
  }
  return null;
}
